import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _classCallCheck from '@babel/runtime/helpers/esm/classCallCheck';
import _createClass from '@babel/runtime/helpers/esm/createClass';
import _inherits from '@babel/runtime/helpers/esm/inherits';
import _possibleConstructorReturn from '@babel/runtime/helpers/esm/possibleConstructorReturn';
import _getPrototypeOf from '@babel/runtime/helpers/esm/getPrototypeOf';
import React, { forwardRef, Component, useEffect, createRef } from 'react';
import { Handler } from 'vega-tooltip';
import * as vega from 'vega';
import { scheme, expressionFunction, parse, View, None } from 'vega';
import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import ContainerDimensions from 'react-container-dimensions';
import mergeOptions from 'merge-options';
import en_US_format from 'd3-format/locale/en-US.json';
import de_DE_format from 'd3-format/locale/de-DE.json';
import fr_FR_format from 'd3-format/locale/fr-FR.json';
import ja_JP_format from 'd3-format/locale/ja-JP.json';
import en_US_time_format from 'd3-time-format/locale/en-US.json';
import de_DE_time_format from 'd3-time-format/locale/de-DE.json';
import fr_FR_time_format from 'd3-time-format/locale/fr-FR.json';
import ja_JP_time_format from 'd3-time-format/locale/ja-JP.json';
import { compile } from 'vega-lite';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { select, event } from 'd3-selection';
import { VennDiagram as VennDiagram$1, sortAreas } from 'venn.js';
import '@spectrum/spectrum-css/dist/components/popover/index.css';
import '@spectrum/spectrum-css/dist/components/popover/colorStops/light.css';
import '@spectrum/spectrum-css/dist/components/dialog/index.css';

var withResponsiveness = function (WrappedComponent) {
  /* eslint-disable react/display-name */
  return /*#__PURE__*/forwardRef(function (props, ref) {
    var width = props.width,
        height = props.height,
        otherProps = _objectWithoutProperties(props, ["width", "height"]);

    var shouldMeasureWidth = width === 'auto';
    var shouldMeasureHeight = height === 'auto'; // Measuring is expensive.  Only do it if necessary.

    if (shouldMeasureWidth || shouldMeasureHeight) {
      return /*#__PURE__*/React.createElement(ContainerDimensions, null, function (_ref) {
        var measuredWidth = _ref.width,
            measuredHeight = _ref.height;
        return /*#__PURE__*/React.createElement(WrappedComponent, _extends({
          ref: ref,
          width: shouldMeasureWidth ? Math.floor(measuredWidth) : width,
          height: shouldMeasureHeight ? Math.floor(measuredHeight) : height
        }, otherProps));
      });
    }

    return /*#__PURE__*/React.createElement(WrappedComponent, _extends({
      ref: ref,
      width: width,
      height: height
    }, otherProps));
  });
};

/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
// Please see the color defined here.
// https://git.corp.adobe.com/Spectrum/spectrum-dna/blob/main/data/aliases/ColorAliases.mjs
// https://git.corp.adobe.com/Spectrum/spectrum-dna/blob/main/data/globals/color/GlobalColors.mjs
var category6 = ['rgb(38, 192, 199)', 'rgb(81, 81, 211)', 'rgb(230, 134, 25)', 'rgb(216, 55, 144)', 'rgb(144, 144, 250)', 'rgb(88, 224, 111)'];
var category12 = ['rgb(38, 192, 199)', 'rgb(81, 81, 211)', 'rgb(230, 134, 25)', 'rgb(216, 55, 144)', 'rgb(144, 144, 250)', 'rgb(88, 224, 111)', 'rgb(38, 128, 235)', 'rgb(111, 56, 177)', 'rgb(223, 191, 0)', 'rgb(203, 111, 16)', 'rgb(38, 142, 108)', 'rgb(155, 236, 84)'];
var category16 = ['rgb(38, 192, 199)', 'rgb(81, 81, 211)', 'rgb(230, 134, 25)', 'rgb(216, 55, 144)', 'rgb(144, 144, 250)', 'rgb(88, 224, 111)', 'rgb(38, 128, 235)', 'rgb(111, 56, 177)', 'rgb(223, 191, 0)', 'rgb(203, 111, 16)', 'rgb(38, 142, 108)', 'rgb(155, 236, 84)', 'rgb(90, 169, 250)', 'rgb(192, 56, 204)', 'rgb(245, 107, 183)', 'rgb(255, 226, 46)'];

/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
var bluegreygreen = ['rgb(28, 70, 114)', 'rgb(97, 128, 159)', 'rgb(193, 193, 193)', 'rgb(74, 142, 24)', 'rgb(28, 80, 2)'];

/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
var ADOBE_CATEGORY_6 = 'adobe_category6';
scheme(ADOBE_CATEGORY_6, category6);
var ADOBE_CATEGORY_12 = 'adobe_category12';
scheme(ADOBE_CATEGORY_12, category12);
var ADOBE_BLUE_GREY_GREEN = 'adobe_bluegreygreen';
scheme(ADOBE_BLUE_GREY_GREEN, {
  scheme: bluegreygreen,
  count: 5,
  extent: [0, 1]
});

/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
var gray50 = 'rgb(255, 255, 255)';
var gray75 = 'rgb(250, 250, 250)';
var gray100 = 'rgb(245, 245, 245)';
var gray200 = 'rgb(234, 234, 234)';
var gray300 = 'rgb(225, 225, 225)';
var gray400 = 'rgb(202, 202, 202)';
var gray500 = 'rgb(179, 179, 179)';
var gray600 = 'rgb(142, 142, 142)';
var gray700 = 'rgb(110, 110, 110)';
var gray800 = 'rgb(75, 75, 75)';
var gray900 = 'rgb(44, 44, 44)';
var blue400 = 'rgb(38, 128, 235)';
var blue500 = 'rgb(20, 115, 230)';
var blue600 = 'rgb(13, 102, 208)';
var blue700 = 'rgb(9, 90, 186)';
var red400 = 'rgb(227, 72, 80)';
var red500 = 'rgb(215, 55, 63)';
var red600 = 'rgb(201, 37, 45)';
var red700 = 'rgb(187, 18, 26)';
var orange400 = 'rgb(230, 134, 25)';
var orange500 = 'rgb(218, 123, 17)';
var orange600 = 'rgb(203, 111, 16)';
var orange700 = 'rgb(189, 100, 13)';
var green400 = 'rgb(45, 157, 120)';
var green500 = 'rgb(38, 142, 108)';
var green600 = 'rgb(18, 128, 92)';
var green700 = 'rgb(16, 113, 84)';
var indigo700 = 'rgb(81, 81, 211)';

/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
var singleColor = category6[0];
var adobeCleanFont = "adobe-clean, 'Source Sans Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Ubuntu, 'Trebuchet MS', 'Lucida Grande', sans-serif";
var spectrumTheme = {
  customFormatTypes: true,
  background: 'transparent',
  arc: {
    fill: singleColor
  },
  area: {
    fill: singleColor,
    opacity: 0.8
  },
  line: {
    stroke: singleColor,
    strokeWidth: 1.5
  },
  path: {
    stroke: singleColor
  },
  rect: {
    fill: singleColor
  },
  bar: {
    fill: singleColor
  },
  rule: {
    stroke: singleColor
  },
  shape: {
    stroke: singleColor
  },
  symbol: {
    fill: singleColor
  },
  text: {
    fill: gray800,
    fontSize: 14,
    font: adobeCleanFont
  },
  legend: {
    symbolType: 'square',
    symbolSize: 324,
    titleFont: adobeCleanFont,
    titleFontSize: 14,
    titleColor: gray800,
    labelFont: adobeCleanFont,
    labelColor: gray700,
    labelFontSize: 14
  },
  title: {
    offset: 10,
    font: adobeCleanFont,
    fontSize: 18,
    color: gray800
  },
  axis: {
    minExtent: 0,
    maxExtent: 200,
    bandPosition: 0.5,
    domain: false,
    domainWidth: 2,
    domainColor: gray800,
    grid: true,
    gridWidth: 1,
    gridColor: gray200,
    gridDash: [],
    gridOpacity: 1,
    labels: true,
    labelAngle: 0,
    labelOverlap: true,
    labelPadding: 8,
    ticks: false,
    tickColor: gray800,
    tickOffset: 0,
    tickRound: true,
    tickSize: 8,
    tickWidth: 1,
    titleColor: gray800,
    titleFont: adobeCleanFont,
    titleFontSize: 14,
    titleAnchor: 'middle',
    titlePadding: 8
  },
  axisBand: {
    grid: false,
    tickExtra: false
  },
  axisY: {
    grid: false,
    labelLimit: 180
  },
  range: {
    category: {
      scheme: ADOBE_CATEGORY_12
    },
    diverging: bluegreygreen,
    heatmap: ['rgb(255, 249, 226)', 'rgb(139, 205, 120)', 'rgb(78, 146, 103)', 'rgb(45, 90, 93)', 'rgb(0, 44, 80)'],
    ordinal: ['rgb(38, 192, 199)', 'rgb(81, 81, 211)', 'rgb(242, 148, 35)', 'rgb(202, 40, 130)', 'rgb(144, 141, 250)', 'rgb(101, 220, 111)', 'rgb(45, 131, 235)', 'rgb(110, 62, 177)', 'rgb(222, 189, 0)', 'rgb(201, 110, 15)', 'rgb(45, 141, 108)', 'rgb(158, 234, 84)']
  },
  style: {
    'guide-label': {
      fill: gray800,
      font: adobeCleanFont,
      fontSize: 12
    },
    'guide-title': {
      fill: gray800,
      font: adobeCleanFont,
      fontSize: 12
    },
    'group-title': {
      fill: gray800,
      font: adobeCleanFont,
      fontSize: 12
    }
  }
};

/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
function capitalize(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/**
 * Canvas is quite a bit simpler than SVG, here we only use the "aria-label" attribute per what I found here:
 * http://pauljadam.com/demos/canvas.html.  As opposed to SVG, we only use one of description or title (favoring
 * description if it is present). The SVG accessibility uses both title and description where is better overall for
 * accessibility.
 *
 * @param {Element} el The root DOM element supplied to Vega where a canvas node is created
 * @param {string} title A chart title
 * @param {string} description A description of the chart. More detailed than title.
 * @returns {Element} The el element which now has accessibility added on the underlying canvas child element
 */
var makeCanvasAccessible = function (el, title, description) {
  // Supporteed roles https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques
  // Current element has role="graphics-document" and aria-roledescription="visualization"
  // aria-roledescription returns undefined for elements with an unsupported role. [graphics-document is unsupported role]
  // https://dequeuniversity.com/rules/axe/3.5/aria-roledescription?application=axeAPI
  // So we removing aria-roledescription="visualization"
  el.removeAttribute('aria-roledescription');
  var canvasEl = el.querySelector('canvas');
  var labelParts = [];

  if (title) {
    labelParts.push(title);
  }

  if (description) {
    labelParts.push(description);
  }

  if (labelParts.length) {
    canvasEl.setAttribute('aria-label', labelParts.join(' '));
  } else {
    canvasEl.removeAttribute('aria-label');
  }

  canvasEl.setAttribute('role', 'img');
  return canvasEl;
};

/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
var lastIdCounter = 0;
/**
 * Generates a new pseudo id with an optional prepended string for context.  This can be used as an `id` attribute for a
 * DOM Element to keep is pseudo unique on a page.
 *
 * @param {string} item A string which is prepended to the ID to give more context to it.
 * @returns {string} A new pseudo id
 */

var generateUID = (function (item) {
  lastIdCounter++;
  var id = "".concat(Math.floor(Math.random() * 10000)).concat(lastIdCounter);

  if (item) {
    return "".concat(item, "-").concat(id);
  }

  return id;
});

/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/**
 * Checks for an accessibility element within a parent and determines whether one needs to be created or removed
 * depending on whether or not we have accessibility text for it. This function is used, as an example, to ensure we
 * have only one "title" element with a unique id attribute within the SVG and not multiple.  It also makes sure to
 * remove a "title" element in the event we don't have any accessibility text to put within it.
 *
 * @param {Element} parentEl The parent DOM element where we'll check for existence of an element of type elementType
 * @param {string} elementType A type of DOM node to add as a child to parentEl if it doesn't already exist and we have textContent
 * @param {string} newId A new pseudo id to try to enforce a unique identifier attribute for the new element
 * @param {string} textContent Accessibility text content to place under the new node of type elementType
 * @returns {Element} The new accessibility element if one was created or null if one was removed
 */

var updateChildAriaNode = function (parentEl, elementType, newId, textContent) {
  var el = parentEl.querySelector(elementType); // We don't currently have a node, but need one

  if (!el && textContent) {
    el = document.createElement(elementType);
    el.setAttribute('id', "".concat(elementType, "-").concat(newId));
    parentEl.appendChild(el);
  } // We have a node, but no longer need it because we don't have accessibility text to put in it anyway


  if (el && !textContent) {
    parentEl.removeChild(el);
    el = null;
  }

  return el;
};
/**
 * Followed a technique detailed here:  https://css-tricks.com/accessible-svgs/#article-header-id-1 (look for the
 * labelledby attribute). This is also exactly how Microsoft's chart-parts project does accessibility with Vega SVGs.
 *
 * @param {Element} el The root DOM element supplied to Vega where it creates an SVG element
 * @param {string} title A chart title
 * @param {string} description A description of the chart. More detailed than title.
 * @returns {Element} The el element which is now populated with accessibility nodes inside the SVG
 */


var makeSVGAccessible = function (el, title, description) {
  // Supported roles https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques
  // Current element has role="graphics-document" and aria-roledescription="visualization"
  // aria-roledescription returns undefined for elements with an unsupported role. [graphics-document is unsupported role]
  // https://dequeuniversity.com/rules/axe/3.5/aria-roledescription?application=axeAPI
  // So we removing aria-roledescription="visualization"
  el.removeAttribute('aria-roledescription');
  var svgEl = el.querySelector('svg');
  var newId = generateUID();
  var titleEl = updateChildAriaNode(svgEl, 'title', newId, title);
  var descriptionEl = updateChildAriaNode(svgEl, 'desc', newId, description);
  var labelledBy = [];

  if (titleEl) {
    titleEl.textContent = title;
    labelledBy.push(titleEl.getAttribute('id'));
  }

  if (descriptionEl) {
    descriptionEl.textContent = description;
    labelledBy.push(descriptionEl.getAttribute('id'));
  }

  if (labelledBy.length) {
    svgEl.setAttribute('aria-labelledby', labelledBy.join(' '));
  } else {
    svgEl.removeAttribute('aria-labelledby');
  }

  svgEl.setAttribute('role', 'img');
  return el;
};

/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/**
 * Performs a post-process on any SVG or Canvas generated by Vega to make it more accessible to screen readers.
 *
 * @param {Element} viewEl The root DOM element supplied to Vega where a canvas/svg node is created
 * @param {string} renderer "canvas" or "svg" depending on which rendering technology is used
 * @param {string} title A chart title
 * @param {string} description A description of the chart. More detailed than title.
 * @returns {Element} The viewEl element which now has accessibility nodes/attributes present
 */

var makeChartAccessible = function (viewEl, renderer, title, description) {
  if (renderer === 'canvas') {
    makeCanvasAccessible(viewEl, title, description);
  }

  if (renderer === 'svg') {
    makeSVGAccessible(viewEl, title, description);
  }
};

/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
var formatByLocale = {
  'en-US': {
    format: en_US_format,
    timeFormat: en_US_time_format
  },
  'de-DE': {
    format: de_DE_format,
    timeFormat: de_DE_time_format
  },
  'fr-FR': {
    format: fr_FR_format,
    timeFormat: fr_FR_time_format
  },
  'ja-JP': {
    format: ja_JP_format,
    timeFormat: ja_JP_time_format
  }
};
/* eslint-enable @typescript-eslint/naming-convention */

var loadLocaleData = function (vega, locale) {
  var localeData = formatByLocale[locale];

  if (localeData) {
    vega.formatLocale(localeData.format);
    vega.timeFormatLocale(localeData.timeFormat);
  } else {
    console.error("Unable to locate locale data for: ".concat(locale, ", using en-US default instead"));
    vega.formatLocale(en_US_format);
    vega.timeFormatLocale(en_US_time_format);
  }
};

/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/**
 * Sorts the options specified in Intl.NumberFormatOptions. This is done to avoid re-generating cached
 * Intl.NumberFormat instances in the event the options keys are identical, but were specified in a
 * different order.
 *
 * @param options Intl.NumberFormatOptions
 * @returns An array of NumberFormatOption parts sorted by prop name
 */
var orderedProps = function (options) {
  return Object.keys(options).sort().map(function (k) {
    return _defineProperty({}, k, options[k]);
  });
};
/**
 * Gets a cached id for a memoized Intl.NumberFormat instance.
 *
 * @param locale Locale string (e.g. "en-US")
 * @param options Intl.NumberFormatOptions
 * @returns A cache id string
 */


var getCacheId = function (options) {
  return JSON.stringify(orderedProps(options));
};
/**
 * Lazily constructs an Intl.NumberFormat object depending on whether or not it already has the object in its
 * cache. Creating the object is costly and caching it speeds up multiple label format calls which happen
 * back-to-back during a chart render or when tooltips are rendered.
 *
 * @param locale Locale string (e.g. "en-US")
 * @param options Intl.NumberFormatOptions which will be fed into an Intl.NumberFormatter instance
 * @param datum A number which should be formatted
 * @returns The formatted label string
 */


var formatNumber = function (locale) {
  var cache = {};
  return {
    getCache: function getCache() {
      return cache;
    },
    format: function format(options, datum) {
      var Formatter;
      var cacheId = getCacheId(options);

      if (cacheId && cache[cacheId] != null) {
        Formatter = cache[cacheId];
      }

      if (!Formatter) {
        Formatter = new Intl.NumberFormat(locale, options);

        if (cacheId) {
          cache[cacheId] = Formatter;
        }
      }

      return Formatter.format(datum);
    }
  };
};

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z$1 = "#vg-tooltip-element.vg-tooltip {\n  padding: 6 8 0.0625rem;\n  border-radius: 4px;\n  position: fixed;\n  z-index: 2000;\n  font-family: Adobe Clean, Helvetica, Arial, sans-serif;\n  font-size: 12px;\n  /* The default look of the tooltip is the same as .light-theme\n    but it can be overwritten by .dark-theme */\n  background-color: rgba(255, 255, 255, 0.9);\n  color: #2c2c2c;\n}\n#vg-tooltip-element.vg-tooltip h2 {\n  font-size: 12px;\n  margin: 0;\n}\n#vg-tooltip-element.vg-tooltip td.key,\n#vg-tooltip-element.vg-tooltip td.value {\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n#vg-tooltip-element.vg-tooltip td.key {\n  color: #707070;\n  max-width: 9.375rem;\n  text-align: right;\n  padding-left: 0;\n}\n#vg-tooltip-element.vg-tooltip td.value {\n  max-width: 12.5rem;\n  text-align: left;\n}\n#vg-tooltip-element.vg-tooltip.dark-theme {\n  background-color: rgba(44, 44, 44, 0.9);\n  border: none;\n  color: #ffffff;\n}\n#vg-tooltip-element.vg-tooltip.dark-theme td.key {\n  color: #cacaca;\n}\n#vg-tooltip-element.vg-tooltip.light-theme {\n  background-color: rgba(255, 255, 255, 0.9);\n  color: #2c2c2c;\n}\n#vg-tooltip-element.vg-tooltip.light-theme td.key {\n  color: #707070;\n}\n";
styleInject(css_248z$1);

function ownKeys$3(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$3(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$3(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$3(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function () { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function noop$1() {// do nothing
}

var Vega = /*#__PURE__*/function (_Component) {
  _inherits(Vega, _Component);

  var _super = _createSuper(Vega);

  function Vega() {
    var _this;

    _classCallCheck(this, Vega);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));
    _this.element = void 0;
    _this.view = void 0;
    return _this;
  }

  _createClass(Vega, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      this.setLocale();
      this.initializeView();
      this.attachToElement();
      this.sizeView();
      this.setViewHover();
      this.setViewTooltip();
      this.renderView();
      this.makeAccessible();
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps) {
      var viewDirty = false;

      if (this.props.spec !== prevProps.spec || this.props.data !== prevProps.data || this.props.renderer !== prevProps.renderer || this.props.logLevel !== prevProps.logLevel || this.props.autoSize !== prevProps.autoSize) {
        this.setLocale();
        this.initializeView();
        this.attachToElement();
        viewDirty = true;
      }

      if (viewDirty || this.props.height !== prevProps.height || this.props.width !== prevProps.width || this.props.padding !== prevProps.padding) {
        this.sizeView();
        viewDirty = true;
      }

      if (viewDirty || this.props.disableHover !== prevProps.disableHover) {
        this.setViewHover();
        viewDirty = true;
      }

      if (viewDirty || this.props.disableTooltip !== prevProps.disableTooltip) {
        this.setViewTooltip();
        viewDirty = true;
      }

      if (viewDirty) {
        this.renderView();
      } // no harm in calling on update. Also, it is not expensive or time consuming call.


      this.makeAccessible();
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.clearView();
    }
  }, {
    key: "setLocale",
    value: function setLocale() {
      var locale = this.props.locale;
      var formatter = formatNumber(locale);
      expressionFunction('IntlNumberFormat', function (datum) {
        var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
        return formatter.format(options, datum);
      });
      loadLocaleData(vega, locale);
    }
  }, {
    key: "initializeView",
    value: function initializeView() {
      var _this2 = this;

      var _this$props = this.props,
          autoSize = _this$props.autoSize,
          spec = _this$props.spec,
          data = _this$props.data,
          renderer = _this$props.renderer,
          logLevel = _this$props.logLevel,
          onParseError = _this$props.onParseError;

      if (autoSize) {
        spec.autosize = autoSize;
      }

      if (this.view) {
        this.clearView();
      }

      if (spec) {
        try {
          var adjustedSpec = _objectSpread$3(_objectSpread$3({}, spec), {}, {
            data: data
          });

          var config = mergeOptions(spectrumTheme, spec.config);
          var runtime = parse(adjustedSpec, config);
          this.view = new View(runtime).renderer(renderer).logLevel(logLevel);

          if (adjustedSpec.signals) {
            adjustedSpec.signals.forEach(function (signal) {
              var listener = _this2.props["onSignal".concat(capitalize(signal.name))];

              if (listener) {
                _this2.view.addSignalListener(signal.name, listener);
              }
            });
          }
        } catch (e) {
          console.error(e);
          this.clearView();
          onParseError(e);
        }
      }
    }
  }, {
    key: "attachToElement",
    value: function attachToElement() {
      if (this.view) {
        this.view.initialize(this.element);
      }
    }
  }, {
    key: "sizeView",
    value: function sizeView() {
      var _this$props2 = this.props,
          width = _this$props2.width,
          height = _this$props2.height,
          padding = _this$props2.padding;

      if (this.view) {
        if (width.step == null) {
          this.view.width(width);
        }

        if (height.step == null) {
          this.view.height(height);
        }

        this.view.padding(padding);
      }
    }
  }, {
    key: "setViewHover",
    value: function setViewHover() {
      var disableHover = this.props.disableHover;

      if (this.view && !disableHover) {
        this.view.hover();
      }
    }
  }, {
    key: "setViewTooltip",
    value: function setViewTooltip() {
      var disableTooltip = this.props.disableTooltip;

      if (this.view && !disableTooltip) {
        var handler = new Handler({
          theme: 'dark'
        });
        this.view.tooltip(handler.call);
      }
    }
  }, {
    key: "clearView",
    value: function clearView() {
      if (this.view) {
        // Removes all event listeners from the view and ensures we avoid memory leaks.
        this.view.finalize();
        this.view = null;
      }
    }
  }, {
    key: "renderView",
    value: function renderView() {
      if (this.view) {
        this.view.run();
      }
    }
  }, {
    key: "makeAccessible",
    value: function makeAccessible() {
      var _this$props3 = this.props,
          renderer = _this$props3.renderer,
          title = _this$props3.title,
          description = _this$props3.description;
      makeChartAccessible(this.view._el, renderer, title, description);
    }
    /**
     *
     * @param {string} signalName The name of the signal you want to update
     * @param {any} signalValue The value you want the signal to be updated to
     */

  }, {
    key: "updateViewSignal",
    value: function updateViewSignal(signalName, signalValue) {
      if (this.view) {
        this.view.signal(signalName, signalValue);
        this.view.runAsync();
      }
    }
  }, {
    key: "render",
    value: function render() {
      var _this3 = this;

      return /*#__PURE__*/React.createElement("div", {
        ref: function ref(el) {
          _this3.element = el;
        },
        className: this.props.className,
        style: this.props.style
      });
    }
  }]);

  return Vega;
}(Component);
Vega.defaultProps = {
  className: '',
  locale: 'en-US',
  renderer: 'canvas',
  logLevel: None,
  width: 300,
  height: 200,
  padding: {
    left: 5,
    right: 5,
    top: 5,
    bottom: 5
  },
  disableHover: false,
  disableTooltip: false,
  onParseError: noop$1,
  autoSize: 'pad'
};
var Vega$1 = withResponsiveness(Vega);

function ownKeys$2(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$2(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$2(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$2(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var VegaLite = function (_ref) {
  var spec = _ref.spec,
      data = _ref.data,
      locale = _ref.locale,
      width = _ref.width,
      height = _ref.height,
      autoSize = _ref.autoSize,
      onParseError = _ref.onParseError,
      postCompile = _ref.postCompile,
      otherProps = _objectWithoutProperties(_ref, ["spec", "data", "locale", "width", "height", "autoSize", "onParseError", "postCompile"]);

  var adjustedSpec = _objectSpread$2({}, spec); // Put the data into the spec, compile it, then pull the data back out.


  if (data) {
    adjustedSpec.data = data;
  }

  if (width) {
    adjustedSpec.width = width;
  }

  if (height) {
    adjustedSpec.height = height;
  }

  var config = mergeOptions(spectrumTheme, adjustedSpec.config);
  var vegaSpec;

  try {
    vegaSpec = compile(adjustedSpec, {
      config: config
    }).spec;

    if (postCompile) {
      vegaSpec = postCompile(vegaSpec);
    }
  } catch (e) {
    onParseError(e);
    return null;
  }

  var _vegaSpec = vegaSpec,
      specData = _vegaSpec.data,
      otherSpecProps = _objectWithoutProperties(_vegaSpec, ["data"]);

  return /*#__PURE__*/React.createElement(Vega$1, _extends({
    autoSize: autoSize,
    locale: locale,
    spec: otherSpecProps,
    data: specData,
    width: width,
    height: height,
    onParseError: onParseError
  }, otherProps));
};

function noop() {// do nothing
}

VegaLite.defaultProps = {
  autoSize: 'pad',
  locale: 'en-US',
  renderer: 'canvas',
  logLevel: None,
  width: 300,
  height: 200,
  padding: {
    left: 5,
    right: 5,
    top: 5,
    bottom: 5
  },
  onParseError: noop,
  disableHover: true
};

var CIRCUMFERENCE = 6.29;
var LESSER_DIMENSION = 'if(width >= height, height, width)';

/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

var createDonutText = function (_ref) {
  var from = _ref.from,
      _ref$valueField = _ref.valueField,
      valueField = _ref$valueField === void 0 ? 'value' : _ref$valueField,
      label = _ref.label;
  // make room for the label mark if label
  var baselineValue = label ? 'bottom' : 'middle';
  var marks = [{
    type: 'text',
    interactive: false,
    from: from,
    encode: {
      enter: {
        x: {
          signal: 'width / 2'
        },
        y: {
          signal: "height / 2 + ".concat(LESSER_DIMENSION, " / 30"),
          round: true
        },
        dy: {
          signal: "".concat(LESSER_DIMENSION, " / 75")
        },
        text: {
          field: valueField
        },
        fontSize: {
          signal: "".concat(LESSER_DIMENSION, " / 4"),
          round: true
        },
        fontWeight: {
          value: 300
        },
        align: {
          value: 'center'
        },
        baseline: {
          value: baselineValue
        }
      }
    }
  }];

  if (label) {
    marks.push({
      type: 'text',
      interactive: false,
      encode: {
        enter: {
          x: {
            signal: 'width / 2'
          },
          y: {
            signal: 'height / 2 + 10'
          },
          text: {
            value: label
          },
          fontSize: {
            signal: "".concat(LESSER_DIMENSION, " / 13"),
            round: true
          },
          fontWeight: {
            value: 'bold'
          },
          align: {
            value: 'center'
          },
          baseline: {
            value: 'top'
          }
        }
      }
    });
  }

  return {
    type: 'group',
    marks: marks
  };
};

/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
var createHoverSignal = function (_ref) {
  var _ref$name = _ref.name,
      name = _ref$name === void 0 ? 'hovered' : _ref$name,
      _ref$valueField = _ref.valueField,
      valueField = _ref$valueField === void 0 ? 'value' : _ref$valueField,
      _ref$defaultValue = _ref.defaultValue,
      defaultValue = _ref$defaultValue === void 0 ? null : _ref$defaultValue;
  return {
    name: name,
    value: defaultValue,
    on: [{
      events: ':mouseover',
      update: "datum.".concat(valueField)
    }, {
      events: ':mouseout',
      update: 'null'
    }]
  };
};

/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
var createClickSignal = function (_ref) {
  var _ref$valueField = _ref.valueField,
      valueField = _ref$valueField === void 0 ? 'value' : _ref$valueField,
      _ref$defaultValue = _ref.defaultValue,
      defaultValue = _ref$defaultValue === void 0 ? null : _ref$defaultValue;
  return {
    name: 'click',
    value: defaultValue,
    on: [{
      events: [{
        type: 'click',
        consume: true,
        source: 'view'
      }],
      update: "datum.".concat(valueField)
    }]
  };
};

var createScales = function (mappings) {
  return {
    name: 'color',
    type: 'ordinal',
    domain: {
      data: 'table',
      field: mappings.color
    },
    range: {
      scheme: ADOBE_CATEGORY_6
    }
  };
};

var createMarks = function (mappings, titles, numberFormat) {
  return [{
    type: 'arc',
    from: {
      data: 'table'
    },
    encode: {
      enter: {
        fill: {
          scale: 'color',
          field: mappings.color
        },
        x: {
          signal: 'width / 2'
        },
        y: {
          signal: 'height / 2'
        },
        tooltip: {
          signal: "{title: datum.".concat(mappings.color, ", '").concat(titles.angleTitle, "': format(datum.").concat(mappings.angle, ", '").concat(numberFormat, "')}")
        }
      },
      update: {
        startAngle: {
          field: 'startAngle'
        },
        endAngle: {
          field: 'endAngle'
        },
        padAngle: {
          value: 0.015
        },
        innerRadius: {
          signal: "".concat(LESSER_DIMENSION, " / 2 * 0.85")
        },
        outerRadius: {
          signal: "".concat(LESSER_DIMENSION, " / 2")
        },
        fillOpacity: {
          signal: "hovered === null ? 1 : hovered === datum.".concat(mappings.color, " ? 1 : 0.25")
        }
      }
    }
  }, createDonutText({
    from: {
      data: 'aggregate'
    },
    valueField: 'sum_fmt',
    label: titles.angleTitle
  })];
};

var generateSpec = function (mappings, titles, legendOrientation, legendDirection, legendColumns, tooltipFormat) {
  var scales = createScales(mappings);
  var marks = createMarks(mappings, titles, tooltipFormat);
  var spec = {
    scales: scales,
    marks: marks,
    legends: [{
      fill: 'color',
      gridAlign: 'all',
      labelLimit: {
        signal: 'width * 0.3'
      },
      orient: legendOrientation,
      direction: legendDirection,
      columns: legendColumns
    }],
    signals: [createHoverSignal({
      name: 'hovered',
      defaultValue: null,
      valueField: mappings.color
    }), createClickSignal({
      defaultValue: null,
      valueField: mappings.color
    })]
  };
  return spec;
};

var VegaDonut = function (_ref) {
  var renderer = _ref.renderer,
      width = _ref.width,
      height = _ref.height,
      autoSize = _ref.autoSize,
      padding = _ref.padding,
      data = _ref.data,
      angle = _ref.angle,
      angleTitle = _ref.angleTitle,
      color = _ref.color,
      sort = _ref.sort,
      legendOrientation = _ref.legendOrientation,
      legendDirection = _ref.legendDirection,
      legendColumns = _ref.legendColumns,
      tooltipNumberFormat = _ref.tooltipNumberFormat,
      titleNumberFormat = _ref.titleNumberFormat,
      clickListener = _ref.clickListener,
      hoveredListener = _ref.hoveredListener,
      otherProps = _objectWithoutProperties(_ref, ["renderer", "width", "height", "autoSize", "padding", "data", "angle", "angleTitle", "color", "sort", "legendOrientation", "legendDirection", "legendColumns", "tooltipNumberFormat", "titleNumberFormat", "clickListener", "hoveredListener"]);

  var mappings = {
    angle: angle,
    color: color
  };
  var tooltipFormat = tooltipNumberFormat !== undefined ? tooltipNumberFormat : ',.2f';
  var titleFormat = titleNumberFormat !== undefined ? titleNumberFormat : '.3s';
  var spec = generateSpec(mappings, {
    angleTitle: angleTitle
  }, legendOrientation, legendDirection, legendColumns, tooltipFormat);
  var vegaData = [{
    name: 'table',
    values: data.data,
    transform: [{
      type: 'pie',
      field: mappings.angle,
      sort: sort
    }]
  }, {
    name: 'aggregate',
    source: 'table',
    transform: [{
      type: 'aggregate',
      fields: [mappings.angle],
      ops: ['sum'],
      as: ['sum']
    }, {
      type: 'formula',
      expr: "format(datum.sum, '".concat(titleFormat, "')"),
      as: 'sum_fmt'
    }]
  }];
  return /*#__PURE__*/React.createElement(Vega$1, _extends({}, {
    onSignalClick: clickListener,
    onSignalHovered: hoveredListener
  }, {
    renderer: renderer,
    width: width,
    height: height,
    padding: padding,
    autoSize: autoSize,
    data: vegaData,
    spec: spec
  }, otherProps));
};

VegaDonut.defaultProps = {
  autoSize: 'fit',
  renderer: 'canvas',
  width: 'auto',
  height: 225,
  padding: {
    left: 0,
    right: 0,
    top: 0,
    bottom: 0
  },
  legendOrientation: 'right',
  legendDirection: 'vertical',
  legendColumns: 1,
  // Default is 1 for vertical, 0 for horizontal
  sort: true
};

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
// Transform type giving errors for some reason

var getTransforms = function (_ref) {
  var actual = _ref.actual,
      remainder = _ref.remainder,
      total = _ref.total;
  // sum up all supplied mappings into single numbers
  var transforms = [{
    type: 'aggregate',
    fields: [actual, total != null ? total : remainder],
    ops: ['sum', 'sum'],
    as: ['actual', total != null ? 'total' : 'remainder']
  }];

  if (remainder != null) {
    // compute the total if we have the remainder
    transforms.push({
      type: 'formula',
      expr: 'datum.actual + datum.remainder',
      as: 'total'
    });
  } else {
    // compute the remainder if we have the total
    transforms.push({
      type: 'formula',
      expr: 'datum.total - datum.actual',
      as: 'remainder'
    });
  }

  return transforms.concat([{
    type: 'formula',
    expr: 'datum.actual / datum.total',
    as: 'actualPercent'
  }, {
    type: 'formula',
    expr: "if(datum.actual < 1000, format(datum.actual, ','), format(datum.actual, '.3s'))",
    as: 'actual_fmt'
  }, {
    type: 'formula',
    expr: "if(datum.total === 0, format(datum.actual, '.0%'), format(datum.actualPercent, '.0%'))",
    as: 'actualPercent_fmt'
  }]);
};

var VegaGauge = function (_ref2) {
  var renderer = _ref2.renderer,
      width = _ref2.width,
      height = _ref2.height,
      padding = _ref2.padding,
      autoSize = _ref2.autoSize,
      data = _ref2.data,
      actual = _ref2.actual,
      remainder = _ref2.remainder,
      total = _ref2.total,
      angleTitle = _ref2.angleTitle,
      showPercent = _ref2.showPercent,
      backgroundFill = _ref2.backgroundFill,
      config = _ref2.config,
      otherProps = _objectWithoutProperties(_ref2, ["renderer", "width", "height", "padding", "autoSize", "data", "actual", "remainder", "total", "angleTitle", "showPercent", "backgroundFill", "config"]);

  var vegaData = [{
    name: 'table',
    values: data.data,
    transform: getTransforms({
      actual: actual,
      remainder: remainder,
      total: total
    })
  }];

  var spec = _objectSpread$1({
    marks: [{
      type: 'arc',
      from: {
        data: 'table'
      },
      encode: {
        enter: {
          fill: {
            value: backgroundFill
          },
          x: {
            signal: 'width / 2'
          },
          y: {
            signal: 'height / 2'
          }
        },
        update: {
          startAngle: {
            signal: "datum.actualPercent * ".concat(CIRCUMFERENCE)
          },
          endAngle: {
            value: CIRCUMFERENCE
          },
          innerRadius: {
            signal: "".concat(LESSER_DIMENSION, " / 2 * 0.85")
          },
          outerRadius: {
            signal: "".concat(LESSER_DIMENSION, " / 2")
          }
        }
      }
    }, {
      type: 'arc',
      from: {
        data: 'table'
      },
      encode: {
        enter: {
          x: {
            signal: 'width / 2'
          },
          y: {
            signal: 'height / 2'
          },
          tooltip: {
            signal: "{title: \"".concat(angleTitle, "\", 'Percent of total': datum.actualPercent_fmt, 'Amount': format(datum.actual, ',')}")
          }
        },
        update: {
          startAngle: {
            value: 0
          },
          endAngle: {
            signal: "datum.actualPercent * ".concat(CIRCUMFERENCE)
          },
          innerRadius: {
            signal: "".concat(LESSER_DIMENSION, " / 2 * 0.85")
          },
          outerRadius: {
            signal: "".concat(LESSER_DIMENSION, " / 2")
          }
        }
      }
    }, createDonutText({
      from: {
        data: 'table'
      },
      valueField: showPercent ? 'actualPercent_fmt' : 'actual_fmt',
      label: angleTitle
    })]
  }, config && {
    config: config
  });

  return /*#__PURE__*/React.createElement(Vega$1, _extends({
    renderer: renderer,
    width: width,
    height: height,
    padding: padding,
    autoSize: autoSize,
    data: vegaData,
    spec: spec
  }, otherProps));
};

VegaGauge.defaultProps = {
  renderer: 'canvas',
  width: 'auto',
  height: 225,
  padding: {
    left: 0,
    right: 0,
    top: 0,
    bottom: 0
  },
  autoSize: 'fit',
  showPercent: false,
  // If true, a percent value is displayed in the center of the donut.
  backgroundFill: gray300
};

var css_248z = ".VennDiagram-module_vennDiagramContainer__3XqzR {\n  width: 100%;\n  height: 100%; }\n  .VennDiagram-module_vennDiagramContainer__3XqzR .spectrum-Popover {\n    padding: 0.5rem;\n    transition: opacity 200ms ease-in, visibility 0ms linear 200ms;\n    transform: translate(-50%, -100%);\n    pointer-events: none; }\n    .VennDiagram-module_vennDiagramContainer__3XqzR .spectrum-Popover.spectrum-Popover--dialog {\n      min-width: 8.125rem; }\n    .VennDiagram-module_vennDiagramContainer__3XqzR .spectrum-Popover .spectrum-Dialog-header {\n      padding-bottom: 0;\n      white-space: nowrap; }\n    .VennDiagram-module_vennDiagramContainer__3XqzR .spectrum-Popover .spectrum-Dialog-header::after {\n      content: none; }\n\n.VennDiagram-module_vennDiagramContent__23cZs {\n  height: 100%;\n  width: 100%; }\n  .VennDiagram-module_vennDiagramContent__23cZs .VennDiagram-module_vennLabel__3DXu_ {\n    position: absolute;\n    text-align: center;\n    transform: translate(-50%, -50%);\n    line-height: 1;\n    pointer-events: none; }\n  .VennDiagram-module_vennDiagramContent__23cZs .venn-area path {\n    fill: #17babe;\n    /* stylelint-disable-line color-no-hex */\n    stroke: white;\n    stroke-width: 2;\n    stroke-opacity: 0;\n    fill-opacity: 0.2;\n    transition: stroke-opacity, fill-opacity 200ms ease-in; }\n    .VennDiagram-module_vennDiagramContent__23cZs .venn-area path.selected {\n      fill-opacity: 0.7;\n      stroke-opacity: 1; }\n  .VennDiagram-module_vennDiagramContent__23cZs .venn-intersection path {\n    stroke: white;\n    stroke-opacity: 0; }\n  .VennDiagram-module_vennDiagramContent__23cZs text {\n    display: none; }\n";
var styles = {"vennDiagramContainer":"VennDiagram-module_vennDiagramContainer__3XqzR","vennDiagramContent":"VennDiagram-module_vennDiagramContent__23cZs","vennLabel":"VennDiagram-module_vennLabel__3DXu_"};
styleInject(css_248z);

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var determineSize = function (el) {
  var _el$node$getBoundingC = el.node().getBoundingClientRect(),
      width = _el$node$getBoundingC.width,
      height = _el$node$getBoundingC.height;

  return [width, height];
};

var updateLabels = function (el, data, textCentres, circles) {
  var labelData = data.map(function (setData) {
    var _circles$setData$sets;

    var setKey = setData.sets.join(',');
    var centerPos = textCentres[setKey] || {};
    var radius = (_circles$setData$sets = circles[setData.sets[0]]) === null || _circles$setData$sets === void 0 ? void 0 : _circles$setData$sets.radius;
    return _objectSpread(_objectSpread({}, setData), {}, {
      pos: _objectSpread(_objectSpread({}, centerPos), {}, {
        width: radius * 2
      })
    });
  }).filter(function (d) {
    return d.label && d.pos.x && d.pos.width;
  }); // only show labels that exist and have position

  var labels = el.selectAll(".".concat(styles.vennLabel)).data(labelData);
  labels = labels.enter().append('span').classed(styles.vennLabel, true).merge(labels);
  labels.exit().remove();
  labels.style('left', function (d) {
    return "".concat(Math.floor(d.pos.x), "px");
  }).style('top', function (d) {
    return "".concat(Math.floor(d.pos.y), "px");
  }).style('width', function (d) {
    return "".concat(Math.floor(d.pos.width), "px");
  }).text(function (d) {
    return d.label;
  });
};

var getTooltipHeadingText = function (data, _ref) {
  var sets = _ref.sets,
      label = _ref.label;

  if (label) {
    return label;
  }

  if (sets) {
    var setLabel = sets.map(function (setIndex) {
      return data[setIndex].label;
    }).filter(Boolean).join(' + ');
    return setLabel;
  }

  return '';
};

var updateTooltip = function (el, data, tooltipContentRenderer) {
  var tooltip = el.select('.spectrum-Popover');
  el.selectAll('g').on('mouseover', function (d, i, nodes) {
    var contentRenderer = tooltipContentRenderer || d.size; // sort all the areas relative to the current item, put hovered item on top

    sortAreas(el, d); // Display a tooltip with the current size

    tooltip.classed('is-open', true);
    tooltip.select('.spectrum-Heading').text(getTooltipHeadingText(data, d));
    tooltip.select('.spectrum-Dialog-content').text(contentRenderer(d));
    var selection = select(nodes[i]);
    selection.select('path').classed('selected', true);
  }).on('mousemove', function () {
    tooltip.style('left', "".concat(event.offsetX, "px")).style('top', "".concat(event.offsetY - 16, "px"));
  }).on('mouseout', function (d, i, nodes) {
    tooltip.classed('is-open', false);
    var selection = select(nodes[i]);
    selection.select('path').classed('selected', false);
  });
};

var update = function (containerEl, contentEl, _ref2) {
  var data = _ref2.data,
      tooltipContentRenderer = _ref2.tooltipContentRenderer;

  var _determineSize = determineSize(contentEl),
      _determineSize2 = _slicedToArray(_determineSize, 2),
      width = _determineSize2[0],
      height = _determineSize2[1];

  var chart = VennDiagram$1().width(width).height(height).wrap(false) // drawing our own html labels because svg text wrapping isn't good
  .normalize(true).styled(false);
  contentEl.datum(data);

  var _chart = chart(contentEl),
      textCentres = _chart.textCentres,
      circles = _chart.circles;

  contentEl.select('svg').attr('data-testid', 'graphic');
  updateLabels(contentEl, data, textCentres, circles);
  updateTooltip(containerEl, data, tooltipContentRenderer);
};

var VennDiagram = function (_ref3) {
  var data = _ref3.data,
      tooltipContentRenderer = _ref3.tooltipContentRenderer;
  var containerEl = /*#__PURE__*/createRef();
  var contentEl = /*#__PURE__*/createRef();
  useEffect(function () {
    if (containerEl.current && contentEl.current) {
      update(select(containerEl.current), select(contentEl.current), {
        data: data,
        tooltipContentRenderer: tooltipContentRenderer
      });
    }
  }, [data]);
  return /*#__PURE__*/React.createElement("div", {
    className: styles.vennDiagramContainer,
    ref: containerEl
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.vennDiagramContent,
    ref: contentEl
  }), /*#__PURE__*/React.createElement("div", {
    className: "spectrum-Popover spectrum-Popover--top spectrum-Popover--withTip spectrum-Popover--dialog spectrum-Dialog--default",
    role: "tooltip",
    "data-testid": "tooltip"
  }, /*#__PURE__*/React.createElement("div", {
    className: "spectrum-Dialog-header spectrum-Dialog-header--default"
  }, /*#__PURE__*/React.createElement("h2", {
    className: "spectrum-Heading spectrum-Heading--subtitle1 spectrum-Dialog-title"
  })), /*#__PURE__*/React.createElement("div", {
    className: "spectrum-Dialog-content"
  }), /*#__PURE__*/React.createElement("div", {
    className: "spectrum-Popover-tip",
    "aria-hidden": "true"
  })));
};

export { ADOBE_BLUE_GREY_GREEN, ADOBE_CATEGORY_12, ADOBE_CATEGORY_6, Vega$1 as Vega, VegaDonut, VegaGauge, VegaLite, VennDiagram, blue400, blue500, blue600, blue700, category12, category16, category6, gray100, gray200, gray300, gray400, gray50, gray500, gray600, gray700, gray75, gray800, gray900, green400, green500, green600, green700, indigo700, orange400, orange500, orange600, orange700, red400, red500, red600, red700, spectrumTheme };
